// Campaigns list. Uses the platform ListTable primitive — gives us mobile
// card view, horizontal overflow, sort and pagination for free. Row actions
// are provided through the `menuOptions` hook ListTable exposes.

import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ListTable, Flex, Loading, theme } from '../../../../ui';
import ConfirmDelete from '../../../../ui/Modal/ConfirmDelete';
import { ACTION, getActions } from '../../utils/campaignActions';
import { rowMetrics } from '../../utils/metricFormatters';
import { useCampaignsActions } from '../../hooks/useCampaignsActions';
import { useCampaignPermissions } from '../../hooks/useCampaignPermissions';
import { CAMPAIGN_STATUS, CHANNEL, OFFER } from '../../constants';
import CampaignNameCell from './CampaignNameCell';
import StatusPill from './StatusPill';
import CampaignsEmptyState from './CampaignsEmptyState';

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
};

const scheduleTop = (campaign, t) => {
  const email = campaign?.schedule?.email;
  const upsell = campaign?.schedule?.networkUpsell;
  if (campaign.status === CAMPAIGN_STATUS.DRAFT) return '—';
  if (email?.sendAt) return formatDate(email.sendAt);
  if (upsell?.activeFrom) return formatDate(upsell.activeFrom);
  return '—';
};

const scheduleHint = (campaign, t) => {
  if (campaign.status === CAMPAIGN_STATUS.DRAFT) return t('campaigns.notScheduled', 'not scheduled');
  if (campaign.status === CAMPAIGN_STATUS.ACTIVE) return t('campaigns.sentRelative', 'Sent 3 days ago');
  if (campaign.status === CAMPAIGN_STATUS.SCHEDULED) return t('campaigns.inXDays', 'in 5 days');
  if (campaign.channels?.includes(CHANNEL.NETWORK_UPSELL) && !campaign.channels.includes(CHANNEL.EMAIL)) return t('campaigns.inApp', 'in-app only');
  return '';
};

const audienceLabel = (campaign, t) => {
  if (campaign.offer === OFFER.INVITATION) return t('campaigns.invitations', 'invitations');
  if (campaign.channels?.includes(CHANNEL.NETWORK_UPSELL) && !campaign.channels.includes(CHANNEL.EMAIL)) return t('campaigns.students', 'students');
  if (campaign.status === CAMPAIGN_STATUS.DRAFT) return t('campaigns.estimated', 'estimated');
  return t('campaigns.recipients', 'recipients');
};

const CampaignsList = ({
  items,
  filters,
  isLoading,
  onClearFilters,
  onCreate,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const permissions = useCampaignPermissions();
  const { updateStatus, duplicateCampaign, deleteCampaign } = useCampaignsActions();
  const [pendingDelete, setPendingDelete] = useState(null);

  const handleRowClick = useCallback((item) => navigate(`/campaigns/${item.id}`), [navigate]);

  const tableInfo = useMemo(() => ([
    {
      label: t('campaigns.col.campaign', 'Campaign'),
      data: (item) => <CampaignNameCell campaign={item} theme={theme} />,
    },
    {
      label: t('campaigns.col.status', 'Status'),
      data: (item) => <StatusPill status={item.status} theme={theme} />,
    },
    {
      label: t('campaigns.col.audience', 'Audience'),
      data: (item) => (
        <Flex direction='column' gap='2'>
          <span style={{ fontWeight: 600 }}>
            {(item.audience?.estimatedReach ?? item.metrics?.recipients ?? 0).toLocaleString()}
          </span>
          <span style={{ fontSize: 12, color: theme.colors.NEU_600 }}>{audienceLabel(item, t)}</span>
        </Flex>
      ),
    },
    {
      label: t('campaigns.col.performance', 'Performance'),
      data: (item) => {
        const metrics = rowMetrics(item);
        return (
          <Flex direction='column' gap='2'>
            <span style={{ fontWeight: 600 }}>{metrics.primary.value}</span>
            <span style={{ fontSize: 12, color: theme.colors.NEU_600 }}>
              {metrics.primary.label}{metrics.primary.rate ? ` · ${metrics.primary.rate}` : ''}
            </span>
          </Flex>
        );
      },
    },
    {
      label: t('campaigns.col.schedule', 'Scheduled'),
      data: (item) => (
        <Flex direction='column' gap='2'>
          <span style={{ fontWeight: 600 }}>{scheduleTop(item, t)}</span>
          <span style={{ fontSize: 12, color: theme.colors.NEU_600 }}>{scheduleHint(item, t)}</span>
        </Flex>
      ),
    },
    {
      label: '',
      menuOptions: (item) => {
        const actions = getActions(item, permissions).filter((a) => a !== ACTION.VIEW);
        return actions.map((action) => {
          switch (action) {
            case ACTION.EDIT:
              return { label: t('globals.edit', 'Edit'), onClick: () => navigate(`/campaigns/${item.id}/edit`) };
            case ACTION.CANCEL:
              return { label: t('campaigns.cancelSchedule', 'Cancel schedule'), onClick: () => updateStatus({ id: item.id, status: CAMPAIGN_STATUS.DRAFT }) };
            case ACTION.PAUSE:
              return { label: t('campaigns.pause', 'Pause'), onClick: () => updateStatus({ id: item.id, status: CAMPAIGN_STATUS.PAUSED }) };
            case ACTION.RESUME:
              return { label: t('campaigns.resume', 'Resume'), onClick: () => updateStatus({ id: item.id, status: CAMPAIGN_STATUS.ACTIVE }) };
            case ACTION.DUPLICATE:
              return { label: t('globals.duplicate', 'Duplicate'), onClick: () => duplicateCampaign(item.id) };
            case ACTION.DELETE:
              return { label: t('globals.delete', 'Delete'), onClick: () => setPendingDelete(item) };
            default:
              return null;
          }
        }).filter(Boolean);
      },
    },
  ]), [t, navigate, permissions, updateStatus, duplicateCampaign]);

  if (isLoading) {
    return <Flex justify='center' align='center' style={{ minHeight: 200 }}><Loading /></Flex>;
  }

  const hasActiveFilters = !!(filters?.search || filters?.status || filters?.channel || filters?.offer);

  if (items.length === 0) {
    return (
      <CampaignsEmptyState
        variant={hasActiveFilters ? 'filtered' : 'zero'}
        onCreate={onCreate}
        onClearFilters={onClearFilters}
      />
    );
  }

  return (
    <>
      <ListTable
        tableName='campaigns'
        data={items}
        tableInfo={tableInfo}
        onRowClick={handleRowClick}
        defaultRowPerPage={25}
      />

      {pendingDelete && (
        <ConfirmDelete
          open={!!pendingDelete}
          title={t('campaigns.deleteTitle', 'Delete campaign')}
          description={t('campaigns.confirmDelete', {
            name: pendingDelete.name,
            defaultValue: "Delete draft '{{name}}'? This can't be undone.",
          })}
          onAcceptDelete={async () => {
            await deleteCampaign(pendingDelete.id);
            setPendingDelete(null);
          }}
          onClose={() => setPendingDelete(null)}
        />
      )}
    </>
  );
};

export default CampaignsList;
